import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type { AuditEntityType, AuditEntry, PagedResult } from '../types';

/**
 * Audit log service (#30). The endpoint is Admin only, so callers pass `enabled` from `isAdmin`.
 * Nothing here invalidates the query: the `MutationCache` in queryClient.ts does, after every mutation.
 */

const PAGE_SIZE = 20;

interface AuditFilter {
  entityType: AuditEntityType;
  entityId: string;
  includeChildren: boolean;
}

const fetchAuditPage = async (filter: AuditFilter, pageNumber: number): Promise<PagedResult<AuditEntry>> => {
  const params = new URLSearchParams({
    entityType: filter.entityType,
    entityId: filter.entityId,
    includeChildren: String(filter.includeChildren),
    pageNumber: String(pageNumber),
    pageSize: String(PAGE_SIZE),
  });
  return api.get<PagedResult<AuditEntry>>(`/audit?${params.toString()}`);
};

/** The history of one entity, newest first, a page at a time */
export const useAuditHistory = (filter: AuditFilter, enabled: boolean) => {
  return useInfiniteQuery({
    queryKey: queryKeys.audit.history(filter.entityType, filter.entityId, filter.includeChildren),
    queryFn: ({ pageParam }) => fetchAuditPage(filter, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.pageNumber < last.totalPages ? last.pageNumber + 1 : undefined),
    enabled: enabled && !!filter.entityId,
  });
};
